import React, { useEffect, useState } from 'react';
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AccessDenied, canAccess } from '../adminAccess';
import { SUPPORT_AGENT_NAME, SUPPORT_EMAIL } from '../companyIdentity';
import DatabaseError from '../components/DatabaseError';
import { useLang } from '../LangContext';
import { statusText } from '../i18n';

// Ticket lifecycle: (no ticket) -> waiting_for_admin -> active -> closed -> (no ticket)
const WAITING = 'waiting_for_admin';
const ACTIVE = 'active';
const CLOSED = 'closed';

const STATUS_STYLE = {
  [WAITING]: 'bg-amber-100 text-amber-800',
  [ACTIVE]: 'bg-emerald-100 text-emerald-800',
  [CLOSED]: 'bg-gray-100 text-gray-600',
};

// Tickets created before the lifecycle existed carry legacy statuses. They must
// still be acceptable/closable, or a real customer conversation is stranded.
const LEGACY_WAITING = ['open', 'pending', 'waiting', 'waiting_customer'];
const LEGACY_CLOSED = ['resolved', 'done'];

function normalizeStatus(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (!v) return WAITING;
  if (v === WAITING || LEGACY_WAITING.includes(v)) return WAITING;
  if (v === ACTIVE) return ACTIVE;
  if (v === CLOSED || LEGACY_CLOSED.includes(v)) return CLOSED;
  return WAITING;
}

function ts(v) {
  if (!v) return '—';
  const d = v.toDate ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function SupportInboxPage({ user }) {
  const { lang } = useLang();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [dbError, setDbError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'support_tickets'), orderBy('updatedAt', 'desc')),
      snap => {
        setDbError('');
        setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      err => {
        console.error(err);
        setDbError(err?.code === 'permission-denied'
          ? 'Permission denied. Please log out and log in again as the super admin.'
          : (err?.message || 'Unable to read support tickets.'));
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return undefined;
    }
    const unsub = onSnapshot(
      query(collection(db, 'support_tickets', selected.id, 'messages'), orderBy('createdAt', 'asc')),
      snap => setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => {
        console.error(err);
        setDbError(err?.code === 'permission-denied'
          ? 'Permission denied. Please log out and log in again as the super admin.'
          : (err?.message || 'Unable to read support messages.'));
      }
    );
    return () => unsub();
  }, [selected]);

  if (!canAccess(user, 'support')) return <AccessDenied />;

  // Accept moves waiting_for_admin -> active and records who owns the case.
  async function acceptTicket() {
    if (!selected || normalizeStatus(selected.status) === CLOSED) return;
    const adminName = user?.name || user?.email || 'PakMazar Support';
    const patch = {
      status: ACTIVE,
      assignedAdminId: user?.uid || user?.email || 'admin',
      assignedAdminName: adminName,
      assignedAdminEmail: user?.email || '',
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await updateDoc(doc(db, 'support_tickets', selected.id), patch);
    setSelected(prev => ({ ...prev, ...patch }));
  }

  // Close is terminal. Nothing in this page may move a ticket out of closed —
  // the customer starts a NEW ticket if they need help again.
  async function closeTicket() {
    if (!selected || normalizeStatus(selected.status) === CLOSED) return;
    if (!window.confirm('Close this support case? The customer can start a new conversation if they need more help.')) return;
    const adminName = user?.name || user?.email || 'PakMazar Support';
    const patch = {
      status: CLOSED,
      closedAt: serverTimestamp(),
      closedBy: user?.uid || user?.email || 'admin',
      closedByName: adminName,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(doc(db, 'support_tickets', selected.id), patch);
    setSelected(prev => ({ ...prev, ...patch }));
  }

  async function sendReply() {
    const body = reply.trim();
    if (!selected || !body) return;
    // A closed case stays closed. This previously flipped closed -> open,
    // silently resurrecting resolved conversations.
    if (normalizeStatus(selected.status) === CLOSED) return;
    // Replying to a waiting ticket implicitly accepts it, so the customer is
    // never left on "waiting for an agent" while an admin is already talking.
    if (normalizeStatus(selected.status) !== ACTIVE) await acceptTicket();
    // senderName is stored for internal/admin history only (it is never
    // rendered to the customer). The customer-visible notification title
    // below uses the masked public agent name instead — see SUPPORT_AGENT_NAME.
    const senderName = user?.name || 'PakMazar Support';
    await addDoc(collection(db, 'support_tickets', selected.id, 'messages'), {
      senderId: user?.email || 'admin',
      senderRole: 'admin',
      senderName,
      body,
      supportEmail: SUPPORT_EMAIL,
      isReadByCustomer: false,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'support_tickets', selected.id), {
      lastMessage: body,
      lastSenderName: senderName,
      supportEmail: SUPPORT_EMAIL,
      contactEmail: SUPPORT_EMAIL,
      routeToEmail: SUPPORT_EMAIL,
      updatedAt: serverTimestamp(),
    });
    if (selected.userId) {
      await addDoc(collection(db, 'notifications'), {
        userId: selected.userId,
        title: `Message from ${SUPPORT_AGENT_NAME}`,
        body,
        supportTicketId: selected.id,
        senderId: user?.email || 'admin',
        senderRole: 'admin',
        senderName: SUPPORT_AGENT_NAME,
        messageType: 'support_reply',
        supportEmail: SUPPORT_EMAIL,
        routeToEmail: SUPPORT_EMAIL,
        isRead: false,
        createdAt: serverTimestamp(),
      });
    }
    setReply('');
  }

  const waitingCount = tickets.filter(t => normalizeStatus(t.status) === WAITING).length;
  const activeCount = tickets.filter(t => normalizeStatus(t.status) === ACTIVE).length;

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="page-title">Support Inbox</h1>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-semibold text-amber-700">{waitingCount} waiting</span>
            {' · '}{activeCount} active{' · '}{tickets.length} total
          </p>
        </div>
      </div>

      <DatabaseError message={dbError} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {tickets.map(t => (
            <button key={t.id} onClick={() => setSelected(t)}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${selected?.id === t.id ? 'bg-primary/5' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm text-gray-900 truncate">{t.customerName || t.userName || t.userId || 'Customer'}</p>
                <span className={`badge ${STATUS_STYLE[normalizeStatus(t.status)]}`}>{statusText(lang, normalizeStatus(t.status))}</span>
              </div>
              <p className="text-xs text-gray-500 truncate mt-1">{t.lastMessage || t.message || t.ticketId}</p>
              <p className="text-[11px] text-gray-400 mt-1">{ts(t.updatedAt || t.createdAt)}</p>
            </button>
          ))}
          {tickets.length === 0 && <p className="text-center text-gray-400 py-16">No support tickets yet.</p>}
        </div>

        <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm min-h-[620px] flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">Select a customer conversation.</div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-900">{selected.ticketId || selected.id}</p>
                    <span className={`badge ${STATUS_STYLE[normalizeStatus(selected.status)]}`}>
                      {statusText(lang, normalizeStatus(selected.status))}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-gray-800 mt-1">{selected.customerName || 'Customer'}</p>
                  <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                    {selected.customerEmail && <p>✉ {selected.customerEmail}</p>}
                    {selected.customerPhone && <p>☎ {selected.customerPhone}</p>}
                    <p>UID: <span className="font-mono">{selected.userId || '—'}</span></p>
                    <p>{(selected.userRole || 'customer')} · {(selected.language || 'az').toUpperCase()}</p>
                    {selected.assignedAdminName && (
                      <p className="text-emerald-700 font-semibold">Handled by {selected.assignedAdminName}</p>
                    )}
                    {normalizeStatus(selected.status) === CLOSED && selected.closedByName && (
                      <p className="text-gray-500">Closed by {selected.closedByName} · {ts(selected.closedAt)}</p>
                    )}
                  </div>
                  {/* Always the current company address — never a per-ticket
                      stored value, so a ticket carrying a legacy/stale
                      supportEmail can never display an outdated address. */}
                  <p className="text-xs text-primary font-semibold mt-1">Routed to: {SUPPORT_EMAIL}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {normalizeStatus(selected.status) === WAITING && (
                    <button className="btn-primary" onClick={acceptTicket}>Accept ticket</button>
                  )}
                  {normalizeStatus(selected.status) !== CLOSED && (
                    <button className="btn-danger" onClick={closeTicket}>Close case</button>
                  )}
                </div>
              </div>

              <div className="flex-1 p-5 overflow-y-auto space-y-3">
                {messages.length === 0 && (
                  <div className="rounded-xl border border-gray-100 p-4">
                    <p className="text-xs text-gray-400 mb-1">Initial message</p>
                    <p className="text-sm text-gray-700">{selected.message || 'No message body saved.'}</p>
                  </div>
                )}
                {messages.map(m => (
                  <div key={m.id} className={`max-w-[76%] rounded-2xl p-3 ${m.senderRole === 'admin' ? 'ml-auto bg-primary text-white' : 'bg-gray-50 border border-gray-100'}`}>
                    <p className="text-xs opacity-75 mb-1">{m.senderName || m.senderRole || 'Customer'} · {ts(m.createdAt)}</p>
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t border-gray-100">
                {normalizeStatus(selected.status) === CLOSED ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    This case is closed. If the customer needs more help they will open a new ticket.
                  </p>
                ) : (
                  <>
                    <textarea className="input-field w-full min-h-[110px] mb-3"
                      placeholder={normalizeStatus(selected.status) === WAITING
                        ? 'Replying will accept this ticket...'
                        : 'Reply as PakMazar Support...'}
                      value={reply} onChange={e => setReply(e.target.value)} />
                    <button className="btn-primary" disabled={!reply.trim()} onClick={sendReply}>Send reply</button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
